import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../components';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import { ButtonSize } from '../Button';
import CustomFilterDropdown from '../CustomFilterDropdown';
import { SelectionFilter } from '../filters';
import { MobileButtonContent, SelectionFilterWrapper, TriggerButton } from './OptionsSelector.styles';

export interface OptionItem<T> {
  label: string;
  value: T;
  selected: boolean;
}

interface OptionsSelectorProps<T> {
  title: string;
  mobileTitle?: string;
  options: OptionItem<T>[];
  loadingOptions?: boolean;
  iconLeft?: ReactNode;
  hideChevron?: boolean;
  triggerButtonSize?: ButtonSize;
  showSelectedCount?: boolean;
  submitButtonTitle?: string;
  /**
   * If provided, a search input will be shown in the bottom sheet with the given placeholder.
   */
  searchInputPlaceholder?: string;
  getMobileTriggerComponent?: (onClick: () => void) => ReactNode;
  onOptionsChange?: (values: T[]) => void;
  onOpen?: () => void;
}

const OptionsSelector = <T,>({
  title,
  mobileTitle,
  options,
  loadingOptions,
  iconLeft,
  hideChevron,
  triggerButtonSize = 'sm',
  showSelectedCount = false,
  submitButtonTitle,
  searchInputPlaceholder,
  getMobileTriggerComponent,
  onOptionsChange,
  onOpen,
}: OptionsSelectorProps<T>) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const [optionsInitial, setOptionsInitial] = useState(options);
  const [optionsLocal, setOptionsLocal] = useState(options);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  // update options if props value is getting changed
  if (JSON.stringify(optionsInitial) !== JSON.stringify(options)) {
    setOptionsLocal(options);
    setOptionsInitial(options);
  }

  const isSubmitButtonDisabled = useMemo(
    () => JSON.stringify(options) === JSON.stringify(optionsLocal),
    [options, optionsLocal],
  );

  const selectedOptionsCount = useMemo(() => options.filter((option) => option.selected).length, [options]);

  const applySelectedOptions = () => {
    onOptionsChange?.(optionsLocal.filter((option) => option.selected).map((option) => option.value));
  };

  const handleClearItemsOnClick = () => {
    setOptionsLocal([]);
    onOptionsChange?.([]);
  };

  const handleOptionSelection = (selected: boolean, value: T) => {
    const optionsTemp = [...optionsLocal];
    const changedItemIndex = optionsTemp.findIndex((option) => option.value === value);
    const changedItem = optionsTemp.find((option) => option.value === value);
    optionsTemp[changedItemIndex] = { ...changedItem, selected };
    setOptionsLocal(optionsTemp);
  };

  const handleBottomSheetOnSubmit = () => {
    applySelectedOptions();
    setIsBottomSheetOpen(false);
  };

  const handleBottomSheetOnClose = () => {
    setOptionsLocal(options);
    setIsBottomSheetOpen(false);
  };

  const handleBottomSheetOnOpen = () => {
    setIsBottomSheetOpen(true);
    onOpen?.();
  };

  return inMobile(width) ? (
    <>
      {getMobileTriggerComponent ? (
        getMobileTriggerComponent(handleBottomSheetOnOpen)
      ) : (
        <TriggerButton onClick={handleBottomSheetOnOpen}>
          <MobileButtonContent>
            {iconLeft}
            {title}
          </MobileButtonContent>
        </TriggerButton>
      )}
      <Modal
        open={isBottomSheetOpen}
        title={mobileTitle ?? title}
        onSubmit={handleBottomSheetOnSubmit}
        onCancel={handleBottomSheetOnClose}
        onClose={handleBottomSheetOnClose}
        submitButtonTitle={submitButtonTitle || t('common.apply')}
        disabledSubmitButton={isSubmitButtonDisabled}
        mobileDisableInnerScroll={!!searchInputPlaceholder}
        mobileShowBorderTop
      >
        <SelectionFilterWrapper>
          <SelectionFilter
            values={optionsLocal}
            loading={loadingOptions}
            multiselect
            onChange={handleOptionSelection}
            reverseCheckboxOrder
            checkboxSizeInPx={24}
            noPadding
            searchInputPlaceholder={searchInputPlaceholder}
            displayCount={false}
          />
        </SelectionFilterWrapper>
      </Modal>
    </>
  ) : (
    <CustomFilterDropdown
      label={title}
      hideChevron={hideChevron}
      iconLeft={iconLeft}
      triggerButtonSize={triggerButtonSize}
      selectedItemsCount={showSelectedCount && selectedOptionsCount}
      submitButtonProps={{
        title: submitButtonTitle || t('common.apply'),
        disabled: isSubmitButtonDisabled,
      }}
      clearItemsOnClick={handleClearItemsOnClick}
      onApply={applySelectedOptions}
      onCancel={() => setOptionsLocal(options)}
      onOpen={onOpen}
    >
      <SelectionFilter
        values={optionsLocal}
        multiselect
        loading={loadingOptions}
        onChange={handleOptionSelection}
        checkboxSizeInPx={16}
        noPadding
        menuMaxHeight="none"
        searchInputPlaceholder={searchInputPlaceholder}
        displayCount
      />
    </CustomFilterDropdown>
  );
};

export default OptionsSelector;
